// Whether an open editor session may edit, decided from the latest read of
// the file — and what a framing page is told about it.
//
// A session the host asked to EDIT stays in edit mode only while the file
// read says so: once someone locks it, it is sent for signing or agreement,
// it is filed, or the person's access drops below editor, the session goes
// read only and says why. A session the host asked to VIEW stays read only.
//
// Messages to the framer (docs/INTEGRATION_GUIDE.md):
//   {type: 'DOCS_EDITOR_STATE', fileId, mode: 'edit'|'view', reason}
//     reason: null | 'locked' | 'signed' | 'agreement' | 'record' | 'no_edit_access'
//   {type: 'DOCS_EDITOR_ERROR', fileId, code: 'no_access'|'not_found'|'error', status}

import { canEditFile, editBlock } from '@/shared/utils/nodeAccess.js';

/** How often an editing session re-reads the file (also on focus). */
export const POLL_MS = 30_000;

/** {mode, reason} for a session asked to `requested` ('edit' | 'view'), given the file read (or null before it). */
export function editorMode(requested, node) {
  if (requested === 'view') return { mode: 'view', reason: null };
  if (!node) return { mode: 'edit', reason: null };
  const block = editBlock(node);
  if (block) return { mode: 'view', reason: block };
  if (!canEditFile(node)) return { mode: 'view', reason: 'no_edit_access' };
  return { mode: 'edit', reason: null };
}

/** A refused read or token request → the error code the framer gets. */
export function failureCode(status) {
  if (status === 403) return 'no_access';
  if (status === 404) return 'not_found';
  return 'error';
}

/** 403/404: the person can't open this file (yet), as opposed to a fault. */
export const isNoAccess = (failure) => failure?.code === 'no_access' || failure?.code === 'not_found';

export function stateMessage(fileId, { mode, reason }) {
  return { type: 'DOCS_EDITOR_STATE', fileId: Number(fileId), mode, reason: reason ?? null };
}

export function errorMessage(fileId, { code, status }) {
  return { type: 'DOCS_EDITOR_ERROR', fileId: Number(fileId), code, status: status ?? null };
}
